import { RequireRole, useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useMemo, useState } from 'react';
import {
  downloadCsvFile,
  flattenToRows,
  parseCsv,
  pickCsvFile,
  rowsToCsv,
  unflattenRows,
} from '../lib/csv';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

// ── Types (mirror apps/api/src/services/scoring) ───────────────────────────────

type ProjectTypeKey = 'new_startup' | 'expansion' | 'retention';

type CategoryKey =
  | 'fiscalReturn'
  | 'jobCreation'
  | 'wageQuality'
  | 'capitalInvestment'
  | 'netFiscalImpact';

interface CategoryWeights {
  fiscalReturn: number;
  jobCreation: number;
  wageQuality: number;
  capitalInvestment: number;
  netFiscalImpact: number;
}

interface DistrictWeights {
  city: number;
  county: number;
  isd: number;
  specialDistrict: number;
}

interface CategoryThreshold {
  excellent: number;
  acceptable: number;
  failing: number;
}

interface FiscalReturnThresholds {
  paybackExcellent: number;
  paybackAcceptable: number;
  paybackFailing: number;
  roiExcellent: number;
  roiAcceptable: number;
  roiFailing: number;
  bcrExcellent: number;
  bcrAcceptable: number;
  bcrFailing: number;
}

interface JobCreationThresholds {
  costPerJobExcellent: number;
  costPerJobAcceptable: number;
  costPerJobFailing: number;
}

interface ScoringThresholds {
  fiscalReturn: FiscalReturnThresholds;
  jobCreation: JobCreationThresholds;
  wageQuality: CategoryThreshold;
  capitalInvestment: CategoryThreshold;
  netFiscalImpact: CategoryThreshold;
}

interface ScoringProfile {
  categoryWeights: CategoryWeights;
  thresholds: ScoringThresholds;
  notApplicable?: CategoryKey[];
}

interface ScoringConfig {
  enabled: boolean;
  benchmarkWage: number;
  districtWeights: DistrictWeights;
  profiles: {
    new_startup: ScoringProfile;
    expansion: ScoringProfile;
    retention: ScoringProfile;
  };
}

// ── Defaults (mirror apps/api seeds) ───────────────────────────────────────────

const BASE_THRESHOLDS: ScoringThresholds = {
  fiscalReturn: {
    paybackExcellent: 5,
    paybackAcceptable: 10,
    paybackFailing: 15,
    roiExcellent: 0.25,
    roiAcceptable: 0.1,
    roiFailing: 0.0,
    bcrExcellent: 2.5,
    bcrAcceptable: 1.5,
    bcrFailing: 1.0,
  },
  jobCreation: {
    costPerJobExcellent: 5_000,
    costPerJobAcceptable: 15_000,
    costPerJobFailing: 30_000,
  },
  wageQuality: { excellent: 150, acceptable: 110, failing: 90 },
  capitalInvestment: { excellent: 100_000, acceptable: 30_000, failing: 10_000 },
  netFiscalImpact: { excellent: 20_000, acceptable: 5_000, failing: 0 },
};

const EXPANSION_THRESHOLDS: ScoringThresholds = {
  ...BASE_THRESHOLDS,
  fiscalReturn: {
    ...BASE_THRESHOLDS.fiscalReturn,
    paybackExcellent: 7,
    paybackAcceptable: 14,
    paybackFailing: 20,
  },
  jobCreation: {
    costPerJobExcellent: 7_500,
    costPerJobAcceptable: 22_500,
    costPerJobFailing: 45_000,
  },
};

const DEFAULT_CONFIG: ScoringConfig = {
  enabled: false,
  benchmarkWage: 52_000,
  districtWeights: { city: 60, county: 20, isd: 15, specialDistrict: 5 },
  profiles: {
    new_startup: {
      categoryWeights: {
        fiscalReturn: 30,
        jobCreation: 25,
        wageQuality: 20,
        capitalInvestment: 15,
        netFiscalImpact: 10,
      },
      thresholds: BASE_THRESHOLDS,
    },
    expansion: {
      categoryWeights: {
        fiscalReturn: 30,
        jobCreation: 25,
        wageQuality: 20,
        capitalInvestment: 15,
        netFiscalImpact: 10,
      },
      thresholds: EXPANSION_THRESHOLDS,
    },
    retention: {
      categoryWeights: {
        fiscalReturn: 25,
        jobCreation: 35,
        wageQuality: 25,
        capitalInvestment: 0,
        netFiscalImpact: 15,
      },
      thresholds: BASE_THRESHOLDS,
      notApplicable: ['capitalInvestment'],
    },
  },
};

const PROJECT_TYPE_LABELS: Record<ProjectTypeKey, string> = {
  new_startup: 'New / Greenfield',
  expansion: 'Expansion',
  retention: 'Retention',
};

const PROJECT_TYPE_DESCRIPTIONS: Record<ProjectTypeKey, string> = {
  new_startup: 'Default rubric for brand-new projects with no existing baseline.',
  expansion:
    'Thresholds relaxed to offset baseline subtraction — expansion projects should not be penalized for smaller incremental denominators.',
  retention:
    'Capital Investment is N/A (its weight redistributes to the remaining categories). Retention grades are advisory pending counterfactual methodology work.',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function pct(n: number) {
  return Math.round(n);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{children}</h3>
  );
}

function WeightSlider({
  label,
  description,
  value,
  onChange,
  total,
  id,
  disabled,
  disabledReason,
}: {
  label: string;
  description: string;
  value: number;
  onChange: (v: number) => void;
  total: number;
  id: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const off = Math.abs(total - 100) > 0.5;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-4">
        <div className="w-44 shrink-0">
          <label
            htmlFor={id}
            className={`text-sm font-medium ${disabled ? 'text-slate-400' : 'text-slate-700'}`}
          >
            {label}
            {disabled && (
              <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                N/A
              </span>
            )}
          </label>
          <p className="text-xs text-slate-400 leading-snug mt-0.5">
            {disabled ? (disabledReason ?? description) : description}
          </p>
        </div>
        <input
          id={id}
          type="range"
          min={0}
          max={100}
          step={1}
          value={pct(value)}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          className="flex-1 disabled:opacity-40"
        />
        <span
          className={`w-12 text-right text-sm font-mono ${disabled ? 'text-slate-400' : off ? 'text-red-500' : 'text-slate-700'}`}
        >
          {disabled ? '—' : `${pct(value)}%`}
        </span>
      </div>
    </div>
  );
}

function ThresholdInputs({
  values,
  keys,
  prefix,
  suffix,
  onChange,
  disabled,
}: {
  values: Record<string, number>;
  keys: string[];
  prefix?: string;
  suffix?: string;
  onChange: (key: string, val: number) => void;
  disabled?: boolean;
}) {
  return (
    <>
      {keys.map((k) => (
        <td key={k} className="py-3 px-2 align-top">
          <div className="flex items-center gap-1">
            {prefix && <span className="text-xs text-slate-400">{prefix}</span>}
            <input
              type="number"
              value={values[k]}
              onChange={(e) => onChange(k, Number(e.target.value))}
              disabled={disabled}
              className="w-16 rounded border border-slate-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50 disabled:text-slate-400"
            />
            {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
          </div>
        </td>
      ))}
    </>
  );
}

function ThresholdRow({
  label,
  description,
  hint,
  values,
  keys,
  prefix,
  suffix,
  onChange,
  disabled,
  disabledReason,
}: {
  label: string;
  description: string;
  hint?: string;
  values: Record<string, number>;
  keys: string[];
  prefix?: string;
  suffix?: string;
  onChange: (key: string, val: number) => void;
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <tr className="border-t border-slate-100">
      <td className="py-3 pr-4 align-top w-40">
        <p className={`text-sm font-semibold ${disabled ? 'text-slate-400' : 'text-slate-800'}`}>
          {label}
          {disabled && (
            <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              N/A
            </span>
          )}
        </p>
        <p
          className={`text-xs mt-0.5 leading-snug ${disabled ? 'text-slate-400' : 'text-slate-500'}`}
        >
          {disabled && disabledReason ? disabledReason : description}
        </p>
        {hint && !disabled && <p className="text-xs text-slate-400 mt-1 italic">{hint}</p>}
      </td>
      <ThresholdInputs
        values={values}
        keys={keys}
        prefix={prefix}
        suffix={suffix}
        onChange={onChange}
        disabled={disabled}
      />
    </tr>
  );
}

function TypeTabButton({
  label,
  active,
  onClick,
  advisory,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  advisory?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
        active
          ? 'border-teal-600 text-teal-700 bg-white'
          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
      {advisory && (
        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
          Advisory
        </span>
      )}
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface SuggestResult {
  suggested: ScoringConfig;
  rationale: string;
  dataUsed: {
    unemploymentRate: number | null;
    medianIncome: number | null;
    povertyRate: number | null;
    benchmarkWage: number;
  };
}

export function SettingsScoringRoute() {
  const org = useCurrentOrg();
  const getToken = useSessionTokenGetter();

  const [config, setConfig] = useState<ScoringConfig>(DEFAULT_CONFIG);
  const [activeType, setActiveType] = useState<ProjectTypeKey>('new_startup');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState<SuggestResult | null>(null);

  const orgId = org?.clerkOrgId ?? null;

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/settings/scoring`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = (await res.json()) as { scoringConfig: ScoringConfig };
        if (!cancelled) setConfig(json.scoringConfig);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getToken]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/settings/scoring`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        const json = (await res.json()) as { error?: string };
        throw new Error(json.error ?? `${res.status}`);
      }
      const json = (await res.json()) as { scoringConfig: ScoringConfig };
      setConfig(json.scoringConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const activeProfile = config.profiles[activeType];
  const notApplicable = useMemo(
    () => new Set<CategoryKey>(activeProfile.notApplicable ?? []),
    [activeProfile.notApplicable],
  );

  // When computing the category-weight total, N/A categories are excluded —
  // their weight is redistributed at scoring time so UI total = 100 across
  // applicable categories, not across the raw five.
  const applicableWeightTotal = (Object.keys(activeProfile.categoryWeights) as CategoryKey[])
    .filter((k) => !notApplicable.has(k))
    .reduce((sum, k) => sum + activeProfile.categoryWeights[k], 0);

  const distTotal = Object.values(config.districtWeights).reduce((a, b) => a + b, 0);

  function updateProfile(patch: Partial<ScoringProfile>) {
    setConfig((c) => ({
      ...c,
      profiles: {
        ...c.profiles,
        [activeType]: { ...c.profiles[activeType], ...patch },
      },
    }));
  }

  function setCatWeight(key: CategoryKey, val: number) {
    updateProfile({
      categoryWeights: { ...activeProfile.categoryWeights, [key]: val },
    });
  }

  function setDistWeight(key: keyof DistrictWeights, val: number) {
    setConfig((c) => ({ ...c, districtWeights: { ...c.districtWeights, [key]: val } }));
  }

  function setFiscalReturnThreshold(key: keyof FiscalReturnThresholds, val: number) {
    updateProfile({
      thresholds: {
        ...activeProfile.thresholds,
        fiscalReturn: { ...activeProfile.thresholds.fiscalReturn, [key]: val },
      },
    });
  }

  function setJobCreationThreshold(key: keyof JobCreationThresholds, val: number) {
    updateProfile({
      thresholds: {
        ...activeProfile.thresholds,
        jobCreation: { ...activeProfile.thresholds.jobCreation, [key]: val },
      },
    });
  }

  function setCategoryThreshold(
    key: 'wageQuality' | 'capitalInvestment' | 'netFiscalImpact',
    field: keyof CategoryThreshold,
    val: number,
  ) {
    updateProfile({
      thresholds: {
        ...activeProfile.thresholds,
        [key]: { ...activeProfile.thresholds[key], [field]: val },
      },
    });
  }

  const weightsOff = Math.abs(applicableWeightTotal - 100) > 0.5;
  const distOff = Math.abs(distTotal - 100) > 0.5;

  function handleDownloadTemplate() {
    const rows = flattenToRows(config);
    downloadCsvFile('ScoringRubric_Template.csv', rowsToCsv(rows));
  }

  async function handleUploadCsv() {
    const text = await pickCsvFile();
    if (!text) return;
    try {
      const parsed = parseCsv(text);
      const nested = unflattenRows(parsed, { coerce: true }) as unknown as ScoringConfig;
      // Light validation: require the three profile keys to exist so we
      // don't silently strip the rubric down to nothing.
      if (
        !nested.profiles?.new_startup ||
        !nested.profiles?.expansion ||
        !nested.profiles?.retention
      ) {
        throw new Error(
          'CSV is missing one or more profile sections (new_startup / expansion / retention).',
        );
      }
      setConfig(nested);
      setError(null);
      setSaved(false);
    } catch (e) {
      setError(`Could not parse CSV: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleSuggest() {
    setSuggesting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/settings/scoring/suggest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = (await res.json()) as { error?: string };
        throw new Error(json.error ?? `${res.status}`);
      }
      const json = (await res.json()) as SuggestResult;
      setSuggestion(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSuggesting(false);
    }
  }

  function applySuggestion() {
    if (!suggestion) return;
    setConfig(suggestion.suggested);
    setSuggestion(null);
    setSaved(false);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Project Scoring</h2>
          <p className="mt-1 text-sm text-slate-500">
            Configure the weighted rubric used to grade project analyses. Each project type (new /
            expansion / retention) carries its own profile. Scores are locked at report generation
            time and never change retroactively.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Download template
          </button>
          <button
            type="button"
            onClick={() => void handleUploadCsv()}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Upload CSV
          </button>
          <button
            type="button"
            onClick={() => void handleSuggest()}
            disabled={suggesting}
            className="rounded-md border border-teal-300 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
          >
            {suggesting ? 'Analyzing community…' : 'Suggest from community data'}
          </button>
        </div>
      </div>

      {suggestion && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-sm font-semibold text-teal-900">AI-suggested rubric</p>
                <p className="mt-1 text-sm text-teal-800">{suggestion.rationale}</p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-teal-700">
                <span>
                  Suggested wage benchmark:{' '}
                  <strong>${suggestion.dataUsed.benchmarkWage.toLocaleString()}/yr</strong>
                </span>
                {suggestion.dataUsed.unemploymentRate != null && (
                  <span>
                    Unemployment: <strong>{suggestion.dataUsed.unemploymentRate}%</strong>
                  </span>
                )}
                {suggestion.dataUsed.medianIncome != null && (
                  <span>
                    Median HH income:{' '}
                    <strong>${suggestion.dataUsed.medianIncome.toLocaleString()}</strong>
                  </span>
                )}
                {suggestion.dataUsed.povertyRate != null && (
                  <span>
                    Poverty rate: <strong>{suggestion.dataUsed.povertyRate}%</strong>
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={applySuggestion}
                className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                className="rounded-md border border-teal-300 bg-white px-3 py-1.5 text-sm font-medium text-teal-700 hover:bg-teal-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <RequireRole
        requiredRole="owner"
        fallback={
          <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 text-sm text-amber-900">
            Scoring settings are visible to Owners only.
          </div>
        }
      >
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <form onSubmit={(e) => void handleSave(e)} className="space-y-6">
            {/* ── Enable toggle ─────────────────────────────────────────── */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Enable Scoring</h3>
                  <p className="mt-0.5 text-xs text-slate-500 max-w-lg">
                    When enabled, each generated report receives a letter grade (A+ through F) based
                    on the rubric below. Grades are locked at generation time — changing these
                    settings won't alter existing reports.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.enabled}
                  onClick={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                    config.enabled ? 'bg-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      config.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </SectionCard>

            {/* ── Benchmark wage (shared across profiles) ───────────────── */}
            <SectionCard>
              <SectionHeading>Wage Benchmark</SectionHeading>
              <p className="mt-1 mb-4 text-xs text-slate-500 max-w-lg">
                Shared across all project types. Area median annual wage used as the baseline for
                wage quality scoring. Set this to the BLS or Census median wage for your primary
                service area. A project paying jobs at 150% of this figure will score "Excellent"
                for wage quality.
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">$</span>
                <input
                  type="number"
                  min={0}
                  step={1000}
                  value={config.benchmarkWage}
                  onChange={(e) =>
                    setConfig((c) => ({ ...c, benchmarkWage: Number(e.target.value) }))
                  }
                  className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-500">per year</span>
              </div>
            </SectionCard>

            {/* ── District weights (shared across profiles) ─────────────── */}
            <SectionCard>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <SectionHeading>Fiscal Return — District Weights</SectionHeading>
                  <p className="mt-1 text-xs text-slate-500 max-w-lg">
                    Shared across all project types. Within the Fiscal Return category, controls how
                    much each taxing district's benefit-cost ratio contributes. Set to 0 for
                    districts that don't apply.
                  </p>
                </div>
                <span
                  className={`text-xs font-mono shrink-0 ml-4 ${distOff ? 'text-red-500 font-semibold' : 'text-slate-400'}`}
                >
                  Total: {pct(distTotal)}%{distOff ? ' — must equal 100%' : ''}
                </span>
              </div>
              <div className="space-y-4">
                <WeightSlider
                  id="dist-weight-city"
                  label="City"
                  description="Municipal government — typically the primary grantor of incentives."
                  value={config.districtWeights.city}
                  onChange={(v) => setDistWeight('city', v)}
                  total={distTotal}
                />
                <WeightSlider
                  id="dist-weight-county"
                  label="County"
                  description="County government — often a co-grantor for Chapter 312/381 abatements."
                  value={config.districtWeights.county}
                  onChange={(v) => setDistWeight('county', v)}
                  total={distTotal}
                />
                <WeightSlider
                  id="dist-weight-isd"
                  label="ISD"
                  description="Independent school district. Set to 0 if not applicable."
                  value={config.districtWeights.isd}
                  onChange={(v) => setDistWeight('isd', v)}
                  total={distTotal}
                />
                <WeightSlider
                  id="dist-weight-special-district"
                  label="Special District"
                  description="MUDs, hospital, navigation, etc. Set to 0 if not applicable."
                  value={config.districtWeights.specialDistrict}
                  onChange={(v) => setDistWeight('specialDistrict', v)}
                  total={distTotal}
                />
              </div>
            </SectionCard>

            {/* ── Project-type tabs ─────────────────────────────────────── */}
            <div className="flex items-end gap-1 border-b border-slate-200">
              {(Object.keys(PROJECT_TYPE_LABELS) as ProjectTypeKey[]).map((t) => (
                <TypeTabButton
                  key={t}
                  label={PROJECT_TYPE_LABELS[t]}
                  active={t === activeType}
                  onClick={() => setActiveType(t)}
                  advisory={t === 'retention'}
                />
              ))}
            </div>

            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              <strong>{PROJECT_TYPE_LABELS[activeType]}:</strong>{' '}
              {PROJECT_TYPE_DESCRIPTIONS[activeType]}
            </div>

            {/* ── Category weights (per-profile) ────────────────────────── */}
            <SectionCard>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <SectionHeading>
                    Category Weights — {PROJECT_TYPE_LABELS[activeType]}
                  </SectionHeading>
                  <p className="mt-1 text-xs text-slate-500 max-w-lg">
                    How much each pillar contributes to the final grade for this project type. N/A
                    categories are excluded — their weight redistributes across the rest at scoring
                    time.
                  </p>
                </div>
                <span
                  className={`text-xs font-mono shrink-0 ml-4 ${weightsOff ? 'text-red-500 font-semibold' : 'text-slate-400'}`}
                >
                  Applicable total: {pct(applicableWeightTotal)}%
                  {weightsOff ? ' — must equal 100%' : ''}
                </span>
              </div>
              <div className="space-y-4">
                <WeightSlider
                  id="weight-fiscal-return"
                  label="Fiscal Return"
                  description="Payback period, ROI, and benefit-cost ratio."
                  value={activeProfile.categoryWeights.fiscalReturn}
                  onChange={(v) => setCatWeight('fiscalReturn', v)}
                  total={applicableWeightTotal}
                  disabled={notApplicable.has('fiscalReturn')}
                />
                <WeightSlider
                  id="weight-job-creation"
                  label={activeType === 'retention' ? 'Employment Impact' : 'Job Creation'}
                  description={
                    activeType === 'retention'
                      ? 'Efficiency of incentive spending per job retained.'
                      : 'Efficiency of incentive spending per job created.'
                  }
                  value={activeProfile.categoryWeights.jobCreation}
                  onChange={(v) => setCatWeight('jobCreation', v)}
                  total={applicableWeightTotal}
                  disabled={notApplicable.has('jobCreation')}
                />
                <WeightSlider
                  id="weight-wage-quality"
                  label="Wage Quality"
                  description="How project wages compare to the area median."
                  value={activeProfile.categoryWeights.wageQuality}
                  onChange={(v) => setCatWeight('wageQuality', v)}
                  total={applicableWeightTotal}
                  disabled={notApplicable.has('wageQuality')}
                />
                <WeightSlider
                  id="weight-capital-investment"
                  label="Capital Investment"
                  description="Capital invested per direct job."
                  disabledReason="Capital Investment does not apply to retention projects."
                  value={activeProfile.categoryWeights.capitalInvestment}
                  onChange={(v) => setCatWeight('capitalInvestment', v)}
                  total={applicableWeightTotal}
                  disabled={notApplicable.has('capitalInvestment')}
                />
                <WeightSlider
                  id="weight-net-fiscal-impact"
                  label="Net Fiscal Impact"
                  description="Long-term net tax revenue per job (10-year NPV)."
                  value={activeProfile.categoryWeights.netFiscalImpact}
                  onChange={(v) => setCatWeight('netFiscalImpact', v)}
                  total={applicableWeightTotal}
                  disabled={notApplicable.has('netFiscalImpact')}
                />
              </div>
            </SectionCard>

            {/* ── Thresholds (per-profile) ──────────────────────────────── */}
            <SectionCard>
              <SectionHeading>
                Category Thresholds — {PROJECT_TYPE_LABELS[activeType]}
              </SectionHeading>
              <p className="mt-1 mb-5 text-xs text-slate-500 max-w-2xl">
                Define what "Excellent," "Acceptable," and "Failing" mean for each metric. Scores
                interpolate smoothly — hitting exactly "Acceptable" earns 65 points; "Excellent"
                earns 100.
                {activeType === 'expansion' && (
                  <>
                    {' '}
                    Expansion thresholds ship relaxed relative to new-startup because baselines are
                    subtracted from the denominators.
                  </>
                )}
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="pb-2 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 w-40">
                        Metric
                      </th>
                      <th className="pb-2 px-2 text-left text-xs font-semibold uppercase tracking-wide text-green-600">
                        Excellent → 100 pts
                      </th>
                      <th className="pb-2 px-2 text-left text-xs font-semibold uppercase tracking-wide text-amber-600">
                        Acceptable → 65 pts
                      </th>
                      <th className="pb-2 px-2 text-left text-xs font-semibold uppercase tracking-wide text-red-500">
                        Failing → 30 pts
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <ThresholdRow
                      label="Payback Period"
                      description="Years until cumulative new tax revenues recover the incentive cost. Lower is better."
                      hint="Typical range: 5–15 yrs."
                      values={
                        activeProfile.thresholds.fiscalReturn as unknown as Record<string, number>
                      }
                      keys={['paybackExcellent', 'paybackAcceptable', 'paybackFailing']}
                      suffix="yrs"
                      onChange={(k, v) =>
                        setFiscalReturnThreshold(k as keyof FiscalReturnThresholds, v)
                      }
                      disabled={notApplicable.has('fiscalReturn')}
                    />
                    <ThresholdRow
                      label="Rate of Return"
                      description="Annualized fiscal return on incentive investment (decimal). Higher is better."
                      hint="0.25 = 25% annual return."
                      values={
                        activeProfile.thresholds.fiscalReturn as unknown as Record<string, number>
                      }
                      keys={['roiExcellent', 'roiAcceptable', 'roiFailing']}
                      onChange={(k, v) =>
                        setFiscalReturnThreshold(k as keyof FiscalReturnThresholds, v)
                      }
                      disabled={notApplicable.has('fiscalReturn')}
                    />
                    <ThresholdRow
                      label="Benefit-Cost Ratio"
                      description="Total tax revenue per dollar of incentive. Higher is better; must exceed 1.0 to break even."
                      hint="2.5× means $1 of incentive generates $2.50 in new taxes."
                      values={
                        activeProfile.thresholds.fiscalReturn as unknown as Record<string, number>
                      }
                      keys={['bcrExcellent', 'bcrAcceptable', 'bcrFailing']}
                      suffix="×"
                      onChange={(k, v) =>
                        setFiscalReturnThreshold(k as keyof FiscalReturnThresholds, v)
                      }
                      disabled={notApplicable.has('fiscalReturn')}
                    />
                    <ThresholdRow
                      label={
                        activeType === 'retention'
                          ? 'Incentive Cost per Job Retained'
                          : 'Incentive Cost per Job'
                      }
                      description="Total incentive value divided by total jobs. Lower is better."
                      hint="Adjust for your budget reality."
                      values={
                        activeProfile.thresholds.jobCreation as unknown as Record<string, number>
                      }
                      keys={['costPerJobExcellent', 'costPerJobAcceptable', 'costPerJobFailing']}
                      prefix="$"
                      onChange={(k, v) =>
                        setJobCreationThreshold(k as keyof JobCreationThresholds, v)
                      }
                      disabled={notApplicable.has('jobCreation')}
                    />
                    <ThresholdRow
                      label="Wage Quality"
                      description="Average project wage as a % of the benchmark wage."
                      hint="150% = jobs paying 1.5× median wage."
                      values={
                        activeProfile.thresholds.wageQuality as unknown as Record<string, number>
                      }
                      keys={['excellent', 'acceptable', 'failing']}
                      suffix="%"
                      onChange={(k, v) =>
                        setCategoryThreshold('wageQuality', k as keyof CategoryThreshold, v)
                      }
                      disabled={notApplicable.has('wageQuality')}
                    />
                    <ThresholdRow
                      label="Capital Investment per Job"
                      description="Total capital investment divided by direct jobs."
                      hint="$100K/job is typical for light manufacturing."
                      disabledReason="Capital Investment does not apply to retention — this category is skipped and its weight redistributes."
                      values={
                        activeProfile.thresholds.capitalInvestment as unknown as Record<
                          string,
                          number
                        >
                      }
                      keys={['excellent', 'acceptable', 'failing']}
                      prefix="$"
                      onChange={(k, v) =>
                        setCategoryThreshold('capitalInvestment', k as keyof CategoryThreshold, v)
                      }
                      disabled={notApplicable.has('capitalInvestment')}
                    />
                    <ThresholdRow
                      label="Net Fiscal Impact per Job"
                      description="10-year NPV of all new tax revenues, divided by total jobs."
                      hint="Size-normalized across small and large deals."
                      values={
                        activeProfile.thresholds.netFiscalImpact as unknown as Record<
                          string,
                          number
                        >
                      }
                      keys={['excellent', 'acceptable', 'failing']}
                      prefix="$"
                      onChange={(k, v) =>
                        setCategoryThreshold('netFiscalImpact', k as keyof CategoryThreshold, v)
                      }
                      disabled={notApplicable.has('netFiscalImpact')}
                    />
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {saved && <p className="text-sm text-green-600">Scoring settings saved.</p>}

            <button
              type="submit"
              disabled={saving || weightsOff || distOff}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save scoring config'}
            </button>
          </form>
        )}
      </RequireRole>
    </div>
  );
}
